import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { Id } from "@/convex/_generated/dataModel";

interface Product {
  _id: Id<"products">;
  brandName: string;
  type: string;
  composition: string;
  price?: string;
  mrp?: string;
}

interface ProductListProps {
  products: Product[] | undefined;
  onEdit: (product: Product) => void;
  onDelete: (id: Id<"products">) => void;
  onCreateNew: () => void;
}

export default function ProductList({ products, onEdit, onDelete, onCreateNew }: ProductListProps) {
  const getWhatsAppLink = (brandName: string) => {
    const message = encodeURIComponent(`I am interested in ${brandName}. Could you provide more information?`);
    return `https://wa.me/919518447302?text=${message}`;
  };

  if (products === undefined) {
    return <div className="text-center py-12">Loading products...</div>;
  }

  if (products.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground mb-4">No products found</p>
          <Button onClick={onCreateNew}>
            <Plus className="h-4 w-4 mr-2" />
            Create Your First Product
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {products.map((product) => (
        <Card key={product._id} className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="text-xl">{product.brandName}</CardTitle>
            <CardDescription>{product.type}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground line-clamp-2">{product.composition}</p>
            <div className="space-y-1">
              {product.mrp && (
                <div className="flex items-baseline gap-2 text-sm">
                  <span className="text-muted-foreground">MRP:</span>
                  <span className="font-semibold">{product.mrp}</span>
                </div>
              )}
              {product.price && (
                <div className="flex items-baseline gap-2 text-sm">
                  <span className="text-muted-foreground">Our Price:</span>
                  <span className="font-bold text-primary">{product.price}</span>
                </div>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <Button size="sm" variant="outline" onClick={() => onEdit(product)} className="flex-1">
                Edit
              </Button>
              <Button size="sm" variant="destructive" onClick={() => onDelete(product._id)} className="flex-1">
                Delete
              </Button>
            </div>
            <Button
              size="sm"
              className="w-full"
              onClick={() => window.open(getWhatsAppLink(product.brandName), "_blank")}
            >
              WhatsApp Inquiry
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
